// Collection metadata (DESIGN.md §3 — the curatorial spine).
//
// The `series` field on each product maps to one of these. Names read as exhibition titles; each
// carries a one-line `statement` (used on shop headers, the landing exhibition block, and product
// cross-sell) and a longer `essay` (the /collections/[slug] curatorial text). `forthcoming` marks
// planned-but-unreleased collections (docs/future_micrographer_collection_materials.json) — teased,
// not shown as populated shows. Copy here is first-draft; owner refines (TODO §C / §B.6).

export interface CollectionMeta {
	/** Matches the catalog `series` string exactly. */
	name: string;
	/** URL slug, e.g. "the-apothecary". */
	slug: string;
	/** One-line curatorial statement (the theme). */
	statement: string;
	/** Curatorial essay for the collection page (paragraphs). */
	essay: string[];
	/** Not yet released — teased, not shown as a populated show. */
	forthcoming?: boolean;
}

// Ordered for display. Live collections first (have pieces in the catalog), forthcoming after.
export const COLLECTIONS: CollectionMeta[] = [
	{
		name: "The Apothecary",
		slug: "the-apothecary",
		statement:
			"The quiet chemistry of the medicine cabinet — aspirin, acetaminophen, melatonin — as luminous, unrepeatable fields of colour.",
		essay: [
			"Every home keeps a small pharmacy: a shelf of white tablets and amber bottles, so familiar we hardly see them. The Apothecary looks closer. Each work in this collection begins with a common medicine — aspirin, acetaminophen, benzocaine, melatonin — dissolved, recrystallized, and photographed beneath a polarized-light microscope.",
			"What emerges bears no resemblance to the pill in your hand. The same molecules that quiet a headache or usher in sleep arrange themselves into cathedrals of colour — fractured golds, deep blues, sudden veins of magenta — that exist for a single moment before the crystal shifts.",
			"These are portraits of the substances we trust with our bodies, rendered as something closer to stained glass than science. Familiar, and yet utterly strange.",
		],
	},
	{
		name: "The Body Electric",
		slug: "the-body-electric",
		statement:
			"The compounds that move us — electrolytes, creatine, amino acids — the chemistry of exertion, crystallized.",
		essay: [
			"The Body Electric is a study of motion held still. Its subjects are the compounds of exertion and recovery — creatine, electrolytes, the amino acids that build and repair muscle — the quiet chemistry behind every effort the body makes.",
			"Crystallized and lit from below, these substances reveal a startling architecture: lattices and spirals, fields that seem to pulse with their own current. There is something fitting in seeing the fuel of movement rendered as pure, electric colour.",
			"Each piece is a single crystallization, unrepeatable, capturing a structure that formed once and then was gone.",
		],
	},
	{
		name: "The Citrus Studies",
		slug: "the-citrus-studies",
		statement:
			"Vitamin C, citric acid, the bright acids of lemon and lime — the everyday sour, rendered radiant.",
		essay: [
			"A squeeze of lemon, the sharpness of an orange, the tang that wakes the tongue — citrus is one of the most ordinary pleasures we know. The Citrus Studies traces that brightness to its source: the acids themselves, vitamin C and citric acid, crystallized and photographed under polarized light.",
			"Freed from the fruit, these acids bloom into radiant fields — sunlit yellows, acid greens, flashes of orange that seem lit from within. The everyday sour becomes something luminous and rare.",
			"It is the transformation at the heart of all our work, distilled: the familiar, made new.",
		],
	},
	{
		name: "The Morning Ritual",
		slug: "the-morning-ritual",
		statement: "Caffeine, matcha, theanine — the substances of the first hour, seen as light.",
		essay: [
			"Before anything else, there is the cup. The Morning Ritual gathers the substances of that first hour — caffeine, matcha, theanine — the quiet chemistry that carries us from sleep into the day.",
			"Under the microscope, caffeine crystallizes into fine, feathered needles; the compounds of tea spread into soft, luminous washes. These are the molecules of alertness and calm, rendered as the light we chase each morning.",
			"Each work is drawn from a single crystallization — a structure formed once, at one moment, and never again.",
		],
	},
	{
		name: "The Companion Series",
		slug: "the-companion-series",
		statement: "Feathers, fur, the traces of the creatures beside us — ethically gathered and crystallized.",
		essay: [
			"The Companion Series turns to the creatures that share our lives, and the small traces they leave behind. Its subjects are gathered ethically and with care — a feather, a strand of fur, the quiet evidence of another living thing beside us.",
			"Under polarized light, these organic materials reveal a fine, luminous structure entirely their own — soft filaments and shifting colour that speak to the intricacy of the living world at its smallest scale.",
			"These are intimate works: portraits, in a sense, of companionship itself.",
		],
	},
	{
		name: "The Scientist",
		slug: "the-scientist",
		statement: "Studies from the bench — the researcher's own reagents and curiosities.",
		essay: [
			"The Scientist is the most personal of the collections — a set of studies drawn from the researcher's own bench. Its subjects are the reagents, dyes, and curiosities that fill a working laboratory: the everyday materials of a life spent looking closely.",
			"These are experiments in the truest sense, capturing substances that resist easy crystallization and reward patience with rare, unexpected structure. Some emerged after many attempts; each is one of a kind.",
			"Together they form a quiet record of the practice behind Micrographer — of curiosity, followed wherever it leads.",
		],
	},
	// --- Forthcoming (planned; teased, not yet populated) ---
	{
		name: "The Cellar",
		slug: "the-cellar",
		statement: "Wine, champagne, the crystalline residue of the vine.",
		essay: [
			"Wine, champagne, and the crystalline residue of the vine — a forthcoming study of the substances of celebration. Arriving soon.",
		],
		forthcoming: true,
	},
	{
		name: "The Sweetness Series",
		slug: "the-sweetness-series",
		statement: "Honey, maple, cane sugar — the architecture of sweetness.",
		essay: [
			"Honey, maple, cane sugar — the architecture of sweetness, crystallized. A forthcoming collection. Arriving soon.",
		],
		forthcoming: true,
	},
	{
		name: "The Textile Studies",
		slug: "the-textile-studies",
		statement: "Silk, wool, cashmere — fibre under polarized light.",
		essay: [
			"Silk, wool, cashmere, linen — natural fibre revealed under polarized light. A forthcoming collection. Arriving soon.",
		],
		forthcoming: true,
	},
	{
		name: "The Skincare Series",
		slug: "the-skincare-series",
		statement: "Niacinamide, hyaluronic films — the chemistry of the vanity.",
		essay: [
			"Niacinamide, hyaluronic films, the quiet actives of the vanity — a forthcoming study of the chemistry of care. Arriving soon.",
		],
		forthcoming: true,
	},
	{
		name: "The Kitchen Laboratory",
		slug: "the-kitchen-laboratory",
		statement: "Salt, vinegar, spice — the pantry, crystallized.",
		essay: [
			"Salt, vinegar, spice, and the everyday alchemy of the pantry — a forthcoming collection. Arriving soon.",
		],
		forthcoming: true,
	},
];

const BY_NAME = new Map(COLLECTIONS.map((c) => [c.name, c]));
const BY_SLUG = new Map(COLLECTIONS.map((c) => [c.slug, c]));

/** Full metadata by series name. */
export const collectionByName = (series: string | null | undefined): CollectionMeta | undefined =>
	series ? BY_NAME.get(series) : undefined;

/** Full metadata by URL slug. */
export const collectionBySlug = (slug: string): CollectionMeta | undefined => BY_SLUG.get(slug);

/** Curatorial statement for a series name, or undefined if unknown. */
export const collectionStatement = (series: string | null | undefined): string | undefined =>
	series ? BY_NAME.get(series)?.statement : undefined;

/** URL slug for a series name (falls back to a slugified name). */
export const collectionSlug = (series: string | null | undefined): string | undefined => {
	if (!series) return undefined;
	return (
		BY_NAME.get(series)?.slug ??
		series
			.toLowerCase()
			.replace(/^the\s+/, "")
			.replace(/\s+/g, "-")
	);
};

/** Display order index for a series (unknown series sort last, stably). */
export const collectionOrder = (series: string | null | undefined): number => {
	if (!series) return COLLECTIONS.length + 1;
	const i = COLLECTIONS.findIndex((c) => c.name === series);
	return i === -1 ? COLLECTIONS.length : i;
};
